/**
 * Cliente API de Gastos (Sprint 17 Fase 2).
 */
import { apiFetch } from './client';

export type EstadoGasto = 'vigente' | 'anulado';

export interface GastoRead {
  id: string;
  categoria_id: string;
  proyecto_id: string | null;
  compensacion_id: string | null;
  monto: number;
  fecha: string;
  proveedor: string;
  descripcion: string;
  num_operacion: string | null;
  comprobante_url: string | null;
  registrado_por_usuario_id: string;
  anulado: boolean;
  motivo_anulacion: string | null;
  anulado_at: string | null;
  anulado_por_usuario_id: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface GastoCreateInput {
  categoria_id: string;
  monto: number;
  fecha: string;
  proveedor: string;
  descripcion: string;
  num_operacion?: string | null;
  comprobante_url?: string | null;
  proyecto_id?: string | null;
  compensacion_id?: string | null;
  // registrado_por_usuario_id NO se envía: el backend lo deriva del JWT.
}

export interface TopCategoriaGasto {
  categoria_id: string;
  nombre: string;
  color: string;
  monto: number;
}

export interface GastoResumen {
  /** Año contable activo (derivado de CierreAnual, no del calendario). */
  anio_activo: number;
  total_mes: number;
  total_anio: number;
  top_categorias: TopCategoriaGasto[];
}

/**
 * KPIs de la pantalla Gastos calculados en backend (misma fuente de verdad
 * que el dashboard: año contable activo, timezone Santiago, exclusión de
 * fondos de terceros). Reemplaza el cálculo en cliente con `new Date()`.
 */
export function getResumenGastos(): Promise<GastoResumen> {
  return apiFetch<GastoResumen>('/gastos/resumen');
}

export function listGastos(
  filtros: {
    fecha_desde?: string;
    fecha_hasta?: string;
    categoria_id?: string;
    incluir_anulados?: boolean;
  } = {},
): Promise<GastoRead[]> {
  const params = new URLSearchParams();
  if (filtros.fecha_desde) params.set('fecha_desde', filtros.fecha_desde);
  if (filtros.fecha_hasta) params.set('fecha_hasta', filtros.fecha_hasta);
  if (filtros.categoria_id) params.set('categoria_id', filtros.categoria_id);
  if (filtros.incluir_anulados)
    params.set('incluir_anulados', String(filtros.incluir_anulados));
  const qs = params.toString();
  return apiFetch<GastoRead[]>(`/gastos/${qs ? `?${qs}` : ''}`);
}

export function crearGasto(data: GastoCreateInput): Promise<GastoRead> {
  return apiFetch<GastoRead>('/gastos/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function anularGasto(id: string, motivo: string): Promise<GastoRead> {
  return apiFetch<GastoRead>(`/gastos/${id}/anular`, {
    method: 'POST',
    body: JSON.stringify({ motivo }),
  });
}

// Sprint 17 S17-Z14: editar gasto existente. El backend rechaza si el
// gasto está anulado o si fue generado por aplicar_compensacion
// (compensacion_id NOT NULL). Para gastos por compensación, anular la
// compensación entera y rehacerla.
export interface GastoUpdateInput {
  categoria_id?: string;
  proyecto_id?: string | null;
  monto?: number;
  fecha?: string;
  proveedor?: string;
  descripcion?: string;
  num_operacion?: string | null;
  comprobante_url?: string | null;
}

export function actualizarGasto(
  id: string,
  data: GastoUpdateInput,
): Promise<GastoRead> {
  return apiFetch<GastoRead>(`/gastos/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
